import { ChevronLeft, ChevronRight } from '@shared/icons';
import { useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { Link } from '../../Link/Link';
import { cn } from '../../shadcn';
import { getPaginationPageLinks, narrowVisibilityClasses } from '../helpers/getVisiblePageRange';

const PAGE_NUMBER_LINK_CLASSES = cn(
  'paragraph-strong flex size-10 items-center justify-center rounded-sm text-foreground transition-colors',
  'hover:bg-container focus-visible:bg-container-hover',
  'aria-[current=page]:bg-container-active aria-[current=page]:text-foreground-inverse',
  'aria-[current=page]:hover:bg-container-active aria-[current=page]:focus-visible:bg-container-active'
);

type MagazinePaginationProps = {
  currentPage: number;
  totalPages: number;
  getPageHref: (page: number) => string;
  className?: string;
  'data-testid'?: string;
};

function MagazinePagination({ currentPage, totalPages, getPageHref, className, 'data-testid': dataTestId = 'magazine-pagination' }: MagazinePaginationProps) {
  const t = useTranslations('magazine.pagination');

  const pageLinks = getPaginationPageLinks(currentPage, totalPages);

  if (pageLinks.length <= 1) {
    return null;
  }

  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <nav aria-label={t('nav_label')} data-track-container="Pagination" data-testid={dataTestId} className={className}>
      <ul className="list-none flex items-center justify-center gap-2">
        {hasPrevious && (
          <li>
            <Link className="mr-2" href={getPageHref(1)} simplified variant="secondary" data-track-name="First" data-testid="magazine-pagination-first">
              {t('first')}
            </Link>
          </li>
        )}
        {hasPrevious && (
          <li>
            <NextLink
              href={getPageHref(currentPage - 1)}
              aria-label={t('previous')}
              data-track-name="Previous"
              data-testid="magazine-pagination-previous"
              className="text-foreground-subtle hover:text-foreground-subtle-hover inline-flex mr-2"
            >
              <ChevronLeft size="medium" />
            </NextLink>
          </li>
        )}
        {pageLinks.map(({ page, tier }) => {
          const isCurrentPage = page === currentPage;
          const visibilityClasses = narrowVisibilityClasses(tier);

          return (
            <li className={visibilityClasses} key={page}>
              <NextLink
                href={getPageHref(page)}
                aria-label={t('page_label', { page })}
                aria-current={isCurrentPage ? 'page' : undefined}
                data-track-name={page}
                data-testid={`magazine-pagination-page-${page}`}
                className={cn(PAGE_NUMBER_LINK_CLASSES, visibilityClasses)}
              >
                {t('page', { page })}
              </NextLink>
            </li>
          );
        })}
        {hasNext && (
          <li>
            <NextLink
              href={getPageHref(currentPage + 1)}
              aria-label={t('next')}
              data-track-name="Next"
              data-testid="magazine-pagination-next"
              className="text-foreground-subtle hover:text-foreground-subtle-hover inline-flex ml-2"
            >
              <ChevronRight size="medium" />
            </NextLink>
          </li>
        )}
        {hasNext && (
          <li>
            <Link className="ml-2" href={getPageHref(totalPages)} simplified variant="secondary" data-track-name="Last" data-testid="magazine-pagination-last">
              {t('last')}
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

export { MagazinePagination, type MagazinePaginationProps };
